import { useCallback, useEffect, useRef, useState } from "react";
import { httpService } from "../services/httpService";

type SchemaStatus = "pending" | "in_progress" | "completed" | "failed";

interface SchemaSetupState {
  progress: number;
  status: SchemaStatus;
  message: string;
  isConnected: boolean;
  usedFallback: boolean;
}

export const useSchemaSetupSocket = (accessToken: string | null): SchemaSetupState => {
  const wsUrl = import.meta.env.VITE_WEB_SOCKET_URL;

  const [state, setState] = useState<SchemaSetupState>({
    progress: 0,
    status: "pending",
    message: "",
    isConnected: false,
    usedFallback: false,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Tracks latest status without putting state in effect deps — prevents multiple WS connections
  const statusRef = useRef<SchemaStatus>("pending");
  const retriesRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    setState((s) => ({ ...s, usedFallback: true }));

    pollRef.current = setInterval(async () => {
      try {
        const data = await httpService.getCurrentUser();
        const setup = data.schema_setup;

        if (!setup || setup.status === "completed") {
          stopPolling();
          statusRef.current = "completed";
          setState((s) => ({
            ...s,
            status: "completed",
            progress: 100,
            message: setup?.message || "Setup complete!",
          }));
        } else if (setup.status === "failed") {
          stopPolling();
          statusRef.current = "failed";
          setState((s) => ({
            ...s,
            status: "failed",
            message: setup.message || "Setup failed",
          }));
        } else {
          statusRef.current = setup.status as SchemaStatus;
          setState((s) => ({
            ...s,
            status: setup.status as SchemaStatus,
            progress: typeof setup.progress === "number" ? setup.progress : s.progress,
            message: setup.message || s.message,
          }));
        }
      } catch {
        // ignore transient poll errors — keep polling
      }
    }, 3000);
  }, [stopPolling]);

  const connect = useCallback(() => {
    if (!accessToken || !wsUrl) return;

    const url = `${wsUrl}/task-progress/?token=${accessToken}&task_id=schema_setup`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      retriesRef.current = 0;
      setState((s) => ({ ...s, isConnected: true }));
      console.log("[SchemaSetupWS] Connected");
    };

    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data || "{}");
        // First message is a connection confirmation — ignore
        if (data.type === "connection") return;
        if (data.type === "progress") {
          const { status, message, progress } = data;
          statusRef.current = status as SchemaStatus;
          setState((s) => ({
            ...s,
            status: status as SchemaStatus,
            message: message || s.message,
            progress: typeof progress === "number" ? progress : s.progress,
          }));
        }
      } catch {
        // ignore parse errors
      }
    };

    ws.onclose = (evt) => {
      wsRef.current = null;
      setState((s) => ({ ...s, isConnected: false }));
      console.log("[SchemaSetupWS] Closed", evt.code);

      // Token/origin errors → fall back to polling, no WS retry
      if (evt.code === 4001 || evt.code === 4003) {
        console.warn("[SchemaSetupWS] Auth/origin error, switching to polling");
        startPolling();
        return;
      }

      // Already finished — no reconnect needed
      if (statusRef.current === "completed" || statusRef.current === "failed") return;

      // Exponential backoff — after 5 retries switch to polling
      if (retriesRef.current < 5) {
        retriesRef.current++;
        const delay = Math.min(1000 * Math.pow(2, retriesRef.current), 30_000);
        console.log(`[SchemaSetupWS] Reconnecting in ${delay}ms (attempt ${retriesRef.current})`);
        setTimeout(connect, delay);
      } else {
        console.warn("[SchemaSetupWS] Max retries reached, switching to polling");
        startPolling();
      }
    };

    ws.onerror = () => {
      // onclose fires after onerror — reconnect logic lives there
    };
  }, [accessToken, wsUrl, startPolling]);

  useEffect(() => {
    if (!accessToken) return;

    connect();

    return () => {
      wsRef.current?.close();
      wsRef.current = null;
      stopPolling();
    };
    // Only re-run when the access token changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  return state;
};
